// ============================================================
// 関東バス GTFS-RT VehiclePosition のポーリング
// ============================================================

import { transit_realtime } from 'gtfs-realtime-bindings'
import type { Entity } from '../types'
import { POLL_VEHICLE_MS } from '../config'
import { fetchKantoBusVehiclePositions } from './odpt-client'
import type { GtfsStatic } from './gtfs-static'

export type VehicleHandler = (vehicles: Entity[]) => void

export class KantoBusRtPoller {
  private timer: ReturnType<typeof setInterval> | null = null
  private handlers = new Set<VehicleHandler>()
  private last: Entity[] = []
  private inFlight = false
  private gtfs: GtfsStatic | null = null

  setGtfsStatic(gtfs: GtfsStatic | null): void {
    this.gtfs = gtfs
  }

  start(): void {
    if (this.timer) return
    void this.pollOnce()
    this.timer = setInterval(() => void this.pollOnce(), POLL_VEHICLE_MS)
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  subscribe(h: VehicleHandler): () => void {
    this.handlers.add(h)
    if (this.last.length) h(this.last)
    return () => this.handlers.delete(h)
  }

  private async pollOnce(): Promise<void> {
    if (this.inFlight) return
    this.inFlight = true
    try {
      const buf = await fetchKantoBusVehiclePositions()
      const feed = transit_realtime.FeedMessage.decode(buf)
      const entities: Entity[] = []
      for (const e of feed.entity) {
        const v = e.vehicle
        if (!v?.position) continue
        const lat = v.position.latitude
        const lon = v.position.longitude
        if (lat == null || lon == null) continue

        const routeId = v.trip?.routeId ?? ''
        const tripId  = v.trip?.tripId ?? ''
        const vehId   = v.vehicle?.id ?? e.id ?? ''

        const shortName = this.gtfs?.getRouteName(routeId) ?? ''
        const headsign  = this.gtfs?.getHeadsign(tripId) ?? ''
        const vehLabel  = (v.vehicle?.label ?? '').trim()
        const label =
          shortName
            ? `関東バス ${shortName}${headsign ? ` ${headsign}` : ''}`
            : vehLabel || (routeId ? `関東バス ${routeId}` : '関東バス')

        entities.push({
          id: `bus:kanto:${vehId}:${routeId}`,
          kind: 'bus',
          lat,
          lon,
          moving: (v.position.speed ?? 0) > 0.5,
          bearing: v.position.bearing ?? undefined,
          label,
          nextEta: (shortName || routeId) ? {
            line: shortName || routeId,
            dest: headsign,
            mins: 99,
          } : undefined,
        })
      }
      this.last = entities
      this.handlers.forEach((h) => h(entities))
    } catch (err) {
      console.warn('[kanto-bus-rt-poller] failed', err)
    } finally {
      this.inFlight = false
    }
  }
}
